import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { mixpanel } from '@/mixpanel'

import type { MultiValue } from 'react-select'
import type { MultiSelectOption } from '@/@bothrs-ui/web/components/forms/select/MultiSelect'
import type { FlowFilters } from '../types/booking.type'

const DEBOUNCE_MS = 300

type UseDoctorFilterParameters = {
  filters: FlowFilters
  onFilterUpdate: (action: 'update-filters', data?: { filters: FlowFilters }) => void
}

function getCheckedDoctorIds(filters: FlowFilters): Set<string> {
  return new Set(
    Object.values(filters.doctors || {})
      .filter((doctor) => doctor.checked)
      .map((doctor) => doctor.agendaId?.toString() ?? ''),
  )
}

/**
 * Manages doctor filter selection for the booking slot view.
 *
 * Local `selectedIds` state is the source of truth for the MultiSelect UI.
 * This prevents the optimistic-then-server-value swap that causes react-select
 * to re-render and lose focus/layout stability.
 *
 * ## How changes flow
 *
 * - **User selects a doctor** → local state updates immediately (optimistic),
 *   then a debounced (300ms) `onFilterUpdate` call fires the API request.
 *   `isLocalChangeRef` ensures the server response doesn't double-sync.
 *
 * - **External filter change** (e.g. FilterModal changes available doctors) →
 *   the `useEffect` on `filters.doctors` syncs local state from the server
 *   response, but only when `isLocalChangeRef` is false.
 *
 * - **Clear all filters** → the consumer calls `clearSelection()` to
 *   immediately reset local state without triggering a debounced API call.
 *   The consumer is responsible for sending its own atomic filter update.
 */
export const useDoctorFilter = ({ filters, onFilterUpdate }: UseDoctorFilterParameters) => {
  // Local source of truth for which doctors are selected.
  // This avoids the optimistic/derived value swap that causes react-select
  // to re-render and lose focus/layout stability.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => getCheckedDoctorIds(filters))

  const filtersRef = useRef(filters)
  filtersRef.current = filters
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Tracks whether the last filters.doctors change was initiated by us
  const isLocalChangeRef = useRef(false)

  const doctorOptions: MultiValue<MultiSelectOption> = useMemo(
    () =>
      Object.values(filters.doctors || {})
        .map((doctor) => ({
          value: doctor.agendaId?.toString() ?? '',
          label: doctor.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'nl', { sensitivity: 'base' })),
    [filters.doctors],
  )

  // Sync from server only for external changes (e.g., FilterModal changes
  // the available doctors). Our own API responses are skipped since local
  // state is already correct.
  useEffect(() => {
    if (isLocalChangeRef.current) {
      // Only clear the flag when no debounce is pending — keeps the guard
      // alive through the entire rapid-click sequence instead of consuming
      // it on the first API response.
      if (!debounceTimerRef.current) {
        isLocalChangeRef.current = false
      }

      return
    }
    setSelectedIds(getCheckedDoctorIds(filters))
  }, [filters.doctors])

  const doctorsValue: MultiValue<MultiSelectOption> = useMemo(
    () => doctorOptions.filter((option) => selectedIds.has(option.value)),
    [doctorOptions, selectedIds],
  )

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  const hasDoctors = Object.keys(filters.doctors || {}).length > 0

  const handleDoctorChange = useCallback(
    (selected: MultiValue<MultiSelectOption>) => {
      const newIds = new Set(selected.map((s) => s.value))
      setSelectedIds(newIds)
      isLocalChangeRef.current = true

      // Cancel any pending debounced API call
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Debounce the API call so rapid clicks batch into a single request
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null
        const currentFilters = filtersRef.current

        const newDoctors = Object.values(currentFilters.doctors || {}).reduce((accumulator, doctor) => {
          return {
            ...accumulator,
            [doctor.agendaId?.toString() ?? '']: {
              ...doctor,
              checked: newIds.has(doctor.agendaId?.toString() ?? ''),
            },
          }
        }, {})

        onFilterUpdate('update-filters', {
          filters: { ...currentFilters, doctors: newDoctors },
        })
      }, DEBOUNCE_MS)

      mixpanel.track('appointment_slotselect_doctor_selected', {
        doctors: selected.map((s) => s.label),
      })
    },
    [onFilterUpdate],
  )

  const hasCheckedDoctor = selectedIds.size > 0

  // Immediately clear the local selection without triggering a debounced API
  // call. Used by onClearFilters which sends its own atomic filter update.
  const clearSelection = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    setSelectedIds(new Set())
    isLocalChangeRef.current = true
  }, [])

  return {
    doctorOptions,
    doctorsValue,
    hasDoctors,
    hasCheckedDoctor,
    handleDoctorChange,
    clearSelection,
  }
}
